import { TASK_STATUS_KANBAN_COLUMNS } from "../data/tasksKanbanData";

export const cx = (...classes) => classes.filter(Boolean).join(" ");

export const normalizeId = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

export const normalizeText = (value = "") =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

export const normalizeTaskStatus = (status = "") => {
  const normalized = normalizeText(status);

  if (
    normalized === "terminee" ||
    normalized === "terminée" ||
    normalized === "terminer" ||
    normalized === "termine"
  ) {
    return "Terminee";
  }

  if (
    normalized === "a faire" ||
    normalized === "afaire" ||
    normalized === "à faire"
  ) {
    return "A faire";
  }

  if (normalized === "en cours") return "En cours";
  if (normalized === "urgent") return "Urgent";

  return "A faire";
};

export const getColumnDroppableId = (productAppId) => {
  return productAppId ? `app-${productAppId}` : "app-null";
};

export const getProductAppIdFromDroppableId = (droppableId) => {
  if (!droppableId || droppableId === "app-null") return null;
  return Number(droppableId.replace("app-", ""));
};

export const isStatusDroppableId = (droppableId = "") => {
  return String(droppableId || "").startsWith("status-");
};

export const getStatusFromDroppableId = (droppableId = "") => {
  const column = TASK_STATUS_KANBAN_COLUMNS.find(
    (item) => item.id === droppableId,
  );

  return column?.status || "A faire";
};

export const formatDateFR = (date) => {
  if (!date) return "";

  try {
    return new Date(date).toLocaleDateString("fr-FR");
  } catch {
    return "";
  }
};

export const formatDateTimeFR = (date) => {
  if (!date) return "";

  try {
    return new Date(date).toLocaleString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
};

export const stripHtml = (html = "") => {
  if (!html) return "";

  const div = document.createElement("div");
  div.innerHTML = html;

  return div.textContent || div.innerText || "";
};

export const getDateTimestamp = (date) => {
  if (!date) return null;

  const parsed = new Date(date);
  const timestamp = parsed.getTime();

  return Number.isNaN(timestamp) ? null : timestamp;
};

export const isTaskDone = (task) => {
  return normalizeTaskStatus(task?.statut) === "Terminee";
};

export const isTaskLate = (task) => {
  if (!task?.due_date) return false;
  if (isTaskDone(task)) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const dueDate = new Date(task.due_date);
  dueDate.setHours(0, 0, 0, 0);

  return dueDate < today;
};

export const getDueDateState = (task) => {
  const timestamp = getDateTimestamp(task?.due_date);

  if (!timestamp) {
    return {
      label: "Sans échéance",
      tone: "muted",
    };
  }

  if (isTaskDone(task)) {
    return {
      label: formatDateFR(task.due_date),
      tone: "done",
    };
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const dueDate = new Date(timestamp);
  dueDate.setHours(0, 0, 0, 0);

  const diffDays = Math.ceil(
    (dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
  );

  if (diffDays < 0) {
    return {
      label: `Retard ${Math.abs(diffDays)} j`,
      tone: "danger",
    };
  }

  if (diffDays === 0) {
    return {
      label: "Aujourd’hui",
      tone: "danger",
    };
  }

  if (diffDays <= 3) {
    return {
      label: `Dans ${diffDays} j`,
      tone: "warning",
    };
  }

  return {
    label: formatDateFR(task.due_date),
    tone: "safe",
  };
};

export const getStatusBadge = (status = "") => {
  const normalized = normalizeTaskStatus(status);

  if (normalized === "Urgent") {
    return {
      label: "Urgent",
      className: "bg-red-100 text-red-700 border-red-200",
    };
  }

  if (normalized === "En cours") {
    return {
      label: "En cours",
      className: "bg-blue-100 text-blue-700 border-blue-200",
    };
  }

  if (normalized === "Terminee") {
    return {
      label: "Terminée",
      className: "bg-emerald-100 text-emerald-700 border-emerald-200",
    };
  }

  return {
    label: "À faire",
    className: "bg-amber-100 text-amber-700 border-amber-200",
  };
};

export const getPriorityBadge = (priority = "NORMAL") => {
  switch (priority) {
    case "URGENT":
      return {
        label: "Urgente",
        className: "bg-red-600 text-white",
        rank: 4,
      };
    case "HIGH":
      return {
        label: "Haute",
        className: "bg-orange-500 text-white",
        rank: 3,
      };
    case "LOW":
      return {
        label: "Basse",
        className: "bg-slate-200 text-slate-700",
        rank: 1,
      };
    default:
      return {
        label: "Normale",
        className: "bg-slate-100 text-slate-700",
        rank: 2,
      };
  }
};

export const getChecklistProgress = (task) => {
  const checklist = Array.isArray(task?.checklist_json)
    ? task.checklist_json
    : [];

  const total = checklist.length;
  const done = checklist.filter((item) => item.done).length;
  const percent = total > 0 ? Math.round((done / total) * 100) : 0;

  return { total, done, percent };
};

export const computePositionFromNeighbors = (tasks = [], destinationIndex = 0) => {
  const previousTask = tasks[destinationIndex - 1];
  const nextTask = tasks[destinationIndex + 1];

  const previousPosition = previousTask
    ? Number(previousTask.position || 0)
    : null;

  const nextPosition = nextTask ? Number(nextTask.position || 0) : null;

  if (previousPosition !== null && nextPosition !== null) {
    return (previousPosition + nextPosition) / 2;
  }

  if (previousPosition !== null) {
    return previousPosition + 1000;
  }

  if (nextPosition !== null) {
    return Math.max(nextPosition / 2, 1);
  }

  return 1000;
};

export const reorderTasks = (list, startIndex, endIndex) => {
  const result = Array.from(list || []);
  const [removed] = result.splice(startIndex, 1);

  result.splice(endIndex, 0, removed);

  return result;
};

export const moveTaskBetweenColumns = ({
  sourceColumn,
  destinationColumn,
  sourceIndex,
  destinationIndex,
}) => {
  const sourceTasks = Array.from(sourceColumn.tasks || []);
  const destinationTasks = Array.from(destinationColumn.tasks || []);
  const [removed] = sourceTasks.splice(sourceIndex, 1);

  destinationTasks.splice(destinationIndex, 0, removed);

  return {
    sourceColumn: {
      ...sourceColumn,
      tasks: sourceTasks,
      count: sourceTasks.length,
    },
    destinationColumn: {
      ...destinationColumn,
      tasks: destinationTasks,
      count: destinationTasks.length,
    },
    movedTask: removed,
    orderedDestinationTasks: destinationTasks,
  };
};

export const compareByPosition = (a, b) => {
  const posA = Number(a.position || 0);
  const posB = Number(b.position || 0);

  if (posA !== posB) return posA - posB;

  return String(a.titre || "").localeCompare(String(b.titre || ""), "fr");
};

export const sortTasks = (tasks = [], filters = {}) => {
  const sortMode = filters.sort_due || "position";

  return [...tasks].sort((a, b) => {
    if (sortMode === "due_asc" || sortMode === "due_desc") {
      const dateA = getDateTimestamp(a.due_date);
      const dateB = getDateTimestamp(b.due_date);

      if (dateA === null && dateB === null) return compareByPosition(a, b);
      if (dateA === null) return 1;
      if (dateB === null) return -1;

      if (dateA !== dateB) {
        return sortMode === "due_asc" ? dateA - dateB : dateB - dateA;
      }

      return compareByPosition(a, b);
    }

    if (sortMode === "priority") {
      const rankA = getPriorityBadge(a.priority).rank;
      const rankB = getPriorityBadge(b.priority).rank;

      if (rankA !== rankB) return rankB - rankA;

      return compareByPosition(a, b);
    }

    return compareByPosition(a, b);
  });
};

export const flattenTasksFromColumns = (columns = []) => {
  const map = new Map();

  (columns || []).forEach((column) => {
    (column.tasks || []).forEach((task) => {
      if (!task?.id) return;

      map.set(Number(task.id), {
        ...task,
        product_app_id:
          task.product_app_id !== undefined
            ? task.product_app_id
            : column.product_app_id,
      });
    });
  });

  return Array.from(map.values());
};

export const taskMatchesFilters = (
  task,
  filters = {},
  { ignoreStatus = false, ignoreProduct = false } = {},
) => {
  const q = String(filters.q || "").trim().toLowerCase();
  const status = filters.status || "all";
  const priority = filters.priority || "all";
  const productAppId = filters.product_app_id || "all";

  const haystack = [
    task.titre,
    task.categorie,
    stripHtml(task.contenu || ""),
    task.association?.nom_asso,
    task.product_app?.nom_app,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  const matchesSearch = q ? haystack.includes(q) : true;

  const matchesStatus =
    ignoreStatus || status === "all"
      ? true
      : normalizeTaskStatus(task.statut) === normalizeTaskStatus(status);

  const matchesPriority =
    priority === "all" ? true : task.priority === priority;

  const matchesProduct =
    ignoreProduct || productAppId === "all"
      ? true
      : productAppId === "null"
        ? task.product_app_id === null || task.product_app_id === undefined
        : Number(task.product_app_id) === Number(productAppId);

  return matchesSearch && matchesStatus && matchesPriority && matchesProduct;
};

export const filterProductColumns = (columns = [], filters = {}) => {
  const productAppId = filters.product_app_id || "all";

  return columns
    .filter((column) => {
      if (productAppId === "all") return true;
      if (productAppId === "null") return column.product_app_id === null;

      return Number(column.product_app_id) === Number(productAppId);
    })
    .map((column) => {
      const filteredTasks = (column.tasks || []).filter((task) =>
        taskMatchesFilters(task, filters, {
          ignoreProduct: true,
        }),
      );

      return {
        ...column,
        mode: "product",
        subtitle: "Application",
        tasks: sortTasks(filteredTasks, filters),
        count: filteredTasks.length,
      };
    });
};

export const buildStatusColumns = (columns = [], filters = {}) => {
  const allTasks = flattenTasksFromColumns(columns).filter((task) =>
    taskMatchesFilters(task, filters, {
      ignoreStatus: true,
    }),
  );

  return TASK_STATUS_KANBAN_COLUMNS.map((statusColumn) => {
    const statusTasks = allTasks.filter(
      (task) =>
        normalizeTaskStatus(task.statut) ===
        normalizeTaskStatus(statusColumn.status),
    );

    const filteredByStatus =
      filters.status && filters.status !== "all"
        ? normalizeTaskStatus(filters.status) ===
          normalizeTaskStatus(statusColumn.status)
        : true;

    const finalTasks = filteredByStatus ? statusTasks : [];

    return {
      ...statusColumn,
      product_app_id: null,
      subtitle: "Statut",
      tasks: sortTasks(finalTasks, filters),
      count: finalTasks.length,
    };
  });
};

export const buildTaskColumns = (columns = [], filters = {}) => {
  if (filters.group_by === "status") {
    return buildStatusColumns(columns, filters);
  }

  return filterProductColumns(columns, filters);
};

export const buildTaskPayload = (formData = {}) => {
  return {
    ...formData,
    asso_id: normalizeId(formData.asso_id),
    product_app_id: normalizeId(formData.product_app_id),
    categorie: formData.categorie || "Général",
    titre: String(formData.titre || "").trim(),
    contenu: formData.contenu || "",
    statut: normalizeTaskStatus(formData.statut || "A faire"),
    priority: formData.priority || "NORMAL",
    due_date: formData.due_date || null,
    cover_url: formData.cover_url || null,
    label_color: formData.label_color || "#facc15",
    labels_json: Array.isArray(formData.labels_json) ? formData.labels_json : [],
    checklist_json: Array.isArray(formData.checklist_json)
      ? formData.checklist_json
      : [],
    members_json: Array.isArray(formData.members_json)
      ? formData.members_json
      : [],
    attachments_json: Array.isArray(formData.attachments_json)
      ? formData.attachments_json
      : [],
    comments_json: Array.isArray(formData.comments_json)
      ? formData.comments_json
      : [],
  };
};